// Presentation only. The owning operation decides whether and how a request may be replayed.
// Recognize the public error envelope structurally, so transport and local errors keep their evidence.
export type ErrorActivity = "read" | "request" | "upload" | "preparation";
export type ErrorRecovery = "retry" | "refresh" | "resume" | "settings" | "wait" | "none";
export type ErrorDiagnostics = { status?: number; code?: string; message: string; details?: unknown };
export type ErrorPresentation = {
  kind: "offline" | "network" | "server" | "stale" | "waiting" | "permission" | "validation" | "preparation" | "conflict" | "unavailable" | "unknown";
  tone: "bad" | "warn";
  title: string;
  message: string;
  dataStatus: string;
  nextAction: string;
  recovery: ErrorRecovery;
  diagnostics: ErrorDiagnostics;
};

function record(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}

export function errorDiagnostics(error: unknown): ErrorDiagnostics {
  return {
    status: record(error) && typeof error.status === "number" && Number.isFinite(error.status) ? error.status : undefined,
    code: record(error) && typeof error.code === "string" ? error.code : undefined,
    message: record(error) && typeof error.message === "string" ? error.message : typeof error === "string" ? error : "No further error message was provided.",
    details: record(error) ? error.details : undefined,
  };
}

const STALE = new Set([
  "plan_revision_stale", "draft_revision_stale", "revision_stale", "workflow_revision_stale", "workflow_definition_stale",
  "dataset_head_changed", "manifest_generation_mismatch", "manifest_inventory_changed", "conflict_set_changed",
  "plan_head_stale", "bulk_conflict_set_changed",
]);
const WAITING = new Set([
  "upload_backpressure", "preparation_in_progress", "upload_completion_started", "file_transfer_active",
  "multipart_attempt_active", "multipart_not_uploading", "multipart_verification_active", "multipart_access_draining",
]);
const SETTINGS = new Set([
  "preparation_source_missing", "preparation_configuration_required", "preparation_configuration_unavailable",
  "canonical_configuration_required", "canonical_snapshot_unavailable", "source_discovery_invalid", "source_discovery_unavailable",
  "source_selection_required", "ambiguous_source", "coordinate_frame_required", "unsupported_format",
]);
const CAPACITY = new Set(["upload_quota_exceeded", "upload_disk_capacity", "upload_file_capacity", "file_too_large", "too_many_files"]);
const SOURCE_CHANGED = new Set([
  "sha256_mismatch", "file_size_mismatch", "multipart_part_changed", "multipart_checksum_mismatch", "multipart_storage_mismatch",
  "manifest_entry_changed", "inventory_entry_changed", "manifest_entry_not_in_inventory", "manifest_inventory_mismatch",
]);
const REQUEST_CHANGED = new Set(["upload_request_changed", "completion_request_changed", "preparation_request_changed", "idempotency_key_reused"]);

/** No retries, navigation or mutations happen here. A missing response never means success or lost data. */
export function presentError(error: unknown, { activity = "request", offline = false }: {
  activity?: ErrorActivity; offline?: boolean;
} = {}): ErrorPresentation {
  const diagnostics = errorDiagnostics(error);
  const { status, code = "", message } = diagnostics;
  const read = activity === "read";
  const upload = activity === "upload";
  const dataStatus = read ? "The latest details could not be loaded."
    : upload ? "Check the server-verified totals below for retained progress. The last request may still need reconciliation."
      : activity === "preparation" ? "Check the current preparation attempt before sending these settings again."
        : "The latest outcome has not been confirmed. Check the current record before submitting again.";
  const build = (copy: Omit<ErrorPresentation, "diagnostics">): ErrorPresentation => ({ ...copy, diagnostics });

  // The direct-storage transport has no platform error envelope. Its bounded error text is
  // produced by putSignedUploadPart; storage access rejection does not imply a project role failure.
  const storageRejected = upload && status === undefined
    ? /^Storage did not accept the upload part \(HTTP (\d{3})\)\.$/.exec(message) : null;
  const storageStatus = storageRejected ? Number(storageRejected[1]) : null;
  if (storageStatus === 401 || storageStatus === 403) return build({
    kind: "permission", tone: "warn", title: "Upload access needs to be renewed",
    message: "Storage did not accept the temporary access for this file part.", dataStatus,
    nextAction: "Refresh the upload status, then resume with the original source to request fresh transfer access. If storage keeps refusing access, ask an administrator to check storage settings.", recovery: "refresh",
  });
  if ((upload && status === undefined && record(error) && error.name === "TimeoutError")
    || (storageStatus !== null && storageStatus >= 500)) return build({
    kind: "server", tone: "warn", title: "Storage did not confirm this file part",
    message: "Celia could not confirm a complete storage response.", dataStatus,
    nextAction: "Refresh the upload status before resuming with the original source. Resume checks retained parts before sending them again.", recovery: "refresh",
  });

  // A structured server response takes precedence over the browser's offline hint.
  const network = status === undefined && (/^(Failed to fetch|fetch failed|NetworkError|Network request failed|Load failed)$/i.test(message)
    || /networkerror when attempting to fetch resource|network connection was lost|connection.*(?:reset|refused)|failed to fetch/i.test(message));
  if (status === undefined && (offline || network)) return build({
    kind: offline ? "offline" : "network", tone: "warn", title: offline ? "You are offline" : "Connection interrupted",
    message: offline ? "Celia cannot reach the server while this device is offline." : "Celia did not receive a response from the server.",
    dataStatus, nextAction: read ? "Reconnect, then reload these details." : upload ? "Reconnect and refresh the upload status. Resume with the original source after checking progress."
      : "Reconnect and refresh the current status before trying the action again.", recovery: read ? "retry" : "refresh",
  });
  if (status === 401 || status === 403) return build({
    kind: "permission", tone: "bad", title: status === 401 ? "Sign in to continue" : "Access required",
    message: status === 401 ? "Your sign-in could not authorize this request." : "Your account does not have permission for this action.",
    dataStatus: "This request was not authorized.", nextAction: status === 401 ? "Sign in again, then reopen this page."
      : "Ask a project administrator for the required access, then reopen this page.", recovery: "none",
  });
  if (status === 404) return build({
    kind: "unavailable", tone: "bad", title: "This item is unavailable", message: "The server could not find the requested item or endpoint.",
    dataStatus: "Its current details could not be confirmed.", nextAction: "Refresh the details, or return to the list and reopen the item. If it is still missing, check access or server availability.", recovery: "refresh",
  });
  if (status === 503 && code === "dataset_storage_unavailable") return build({
    kind: "server", tone: "warn", title: "Dataset storage needs attention",
    message: "Celia could not initialize storage for the new dataset.",
    dataStatus: "No new dataset was registered by this request.",
    nextAction: "Ask an administrator to check storage connectivity and publication settings. Retry creating the dataset after those settings are working.", recovery: "settings",
  });
  if (status === 503 && code === "storage_capacity_unavailable") return build({
    kind: "server", tone: "warn", title: "Server storage is full",
    message: "Celia cannot continue while server storage has no available space.", dataStatus,
    nextAction: read ? "Ask an administrator to restore storage capacity, then reload these details."
      : "Ask an administrator to restore storage capacity, then refresh the current status before continuing. Keep the original source and upload session when resuming.",
    recovery: read ? "retry" : "refresh",
  });
  if (status === 503 && code === "database_busy") return build({
    kind: "waiting", tone: "warn", title: "The server is busy",
    message: "Celia cannot confirm a complete response while server capacity is in use.", dataStatus,
    nextAction: read ? "Wait a moment, then reload these details."
      : "Refresh the current status before submitting again. Keep the original upload session and source when resuming.",
    recovery: read ? "retry" : "refresh",
  });
  if (code === "upload_session_expired" || code === "upload_expired") return build({
    kind: "unavailable", tone: "bad", title: "This upload session has expired", message: "This session can no longer accept files.",
    dataStatus: "Expired sessions cannot be resumed.", nextAction: "Start a new upload with the original source files.", recovery: "none",
  });
  if (STALE.has(code)) return build({
    kind: "stale", tone: "warn", title: "The current version has changed", message: "The server refused an action based on an older revision or file manifest.",
    dataStatus: "Review the latest server state before making another change.", nextAction: "Refresh the details, review what changed, and apply your choice to the current revision.", recovery: "refresh",
  });
  if (REQUEST_CHANGED.has(code)) return build({
    kind: "conflict", tone: "bad", title: "An earlier request used different settings", message: "The same request key cannot be used for two different actions.",
    dataStatus: "The earlier request keeps its original identity and outcome.", nextAction: "Refresh the current status. Keep the original settings when resuming, or start a separate action for new settings.", recovery: "refresh",
  });
  if (SETTINGS.has(code)) return build({
    kind: "preparation", tone: "bad", title: "Preparation needs your input", message: "Celia cannot prepare this source with the currently available settings or evidence.",
    dataStatus: "Transferred files and prepared data are separate. This response does not confirm a usable prepared snapshot.",
    nextAction: "Open Preparation settings and findings. Choose the source, format and coordinate frame requested there, then start a new attempt.", recovery: "settings",
  });
  if (WAITING.has(code) || status === 423 || status === 429) {
    const seconds = record(diagnostics.details) && typeof diagnostics.details.retry_after_seconds === "number"
      && Number.isFinite(diagnostics.details.retry_after_seconds) && diagnostics.details.retry_after_seconds > 0
      ? Math.ceil(diagnostics.details.retry_after_seconds) : null;
    return build({
      kind: "waiting", tone: "warn", title: code === "preparation_in_progress" ? "Preparation is already running"
        : WAITING.has(code) && code !== "upload_backpressure" ? "Waiting for the active operation" : "Waiting for available capacity",
      message: code === "preparation_in_progress" ? "Another preparation attempt is queued or running on this input."
        : WAITING.has(code) && code !== "upload_backpressure" ? "Another transfer or verification step is using this item."
          : "The server cannot accept this request while the resource is busy.",
      dataStatus: "This response does not confirm completion. Check the current progress before continuing.",
      nextAction: `${seconds ? `Wait at least ${seconds} ${seconds === 1 ? "second" : "seconds"}, then` : "Wait for the active operation or capacity to become available, then"} refresh the status.${upload ? " Resume this upload when it is available." : ""}`,
      recovery: "wait",
    });
  }
  if (CAPACITY.has(code) || status === 413) return build({
    kind: "validation", tone: "bad", title: "The upload exceeds an available limit", message: "The source cannot be accepted with the current file, storage or project limit.",
    dataStatus: "Check the session's verified totals before changing the source.", nextAction: "Review the limit in Technical details. Reduce the source for a new upload, or ask an administrator to make capacity available.", recovery: "none",
  });
  if (SOURCE_CHANGED.has(code)) return build({
    kind: "validation", tone: "bad", title: "The file bytes do not match this upload", message: "The file size or content differs from the source this session expects.",
    dataStatus: "Mismatched bytes are not accepted as verified files.", nextAction: "Reselect the original, unchanged source to resume. Use a new upload if you intended to change the files.", recovery: "resume",
  });
  if (code === "upload_paused") return build({
    kind: "waiting", tone: "warn", title: "The upload is paused", message: "This session must be resumed before it can accept more files.",
    dataStatus: "Check the server-verified totals below for retained progress.", nextAction: "Reselect the original source if needed, then use Resume upload.", recovery: "resume",
  });
  if (status === 409 && code === "upload_client_upgrade_required") return build({
    kind: "unavailable", tone: "warn", title: "Client update needed",
    message: "Use an updated Celia client.", dataStatus: "This request did not change the upload.",
    nextAction: "Update, then reopen this upload.", recovery: "none",
  });
  if ((status === 400 || status === 422) && read) return build({
    kind: "validation", tone: "bad", title: "These details could not be loaded",
    message: "The server rejected the request to load these details.", dataStatus,
    nextAction: "Reload the details. If this continues, report the error in Technical details.", recovery: "retry",
  });
  if (status === 400 || status === 422) return build({
    kind: "validation", tone: "bad", title: "Some input needs to be corrected", message: "The server could not accept the supplied files or settings.",
    dataStatus: "This response does not confirm the requested result.", nextAction: "Review the form and findings. Technical details contains the exact reason to correct before submitting again.", recovery: "settings",
  });
  if (status === 409) return build({
    kind: "conflict", tone: "warn", title: "This action needs the current state", message: "The current server state does not allow this request.",
    dataStatus: "This response does not confirm the requested result.", nextAction: "Refresh the status and check the pending steps. Technical details contains the exact condition to resolve before continuing.", recovery: "refresh",
  });
  if (status === 408 || status === 504 || (status !== undefined && status >= 500)) return build({
    kind: "server", tone: "warn", title: status === 408 || status === 504 ? "The request timed out" : "The server could not finish this request",
    message: "Celia could not confirm a complete response.", dataStatus,
    nextAction: read ? "Wait a moment, then reload these details." : upload ? "Refresh the upload status before resuming with the original source."
      : "Refresh the current status before sending another request.", recovery: read ? "retry" : "refresh",
  });
  return build({
    kind: "unknown", tone: "bad", title: read ? "These details could not be loaded" : "This action could not be completed",
    message: "Celia encountered a problem while handling this action.", dataStatus,
    nextAction: read ? "Reload the details. If the problem continues, use Technical details when reporting it."
      : "Check the current status and Technical details before trying again.", recovery: read ? "retry" : "refresh",
  });
}

/** Diagnostic objects can be cyclic or non-JSON local exceptions; never crash the recovery UI. */
export function diagnosticDetails(details: unknown): string | null {
  if (details === undefined || details === null) return null;
  try { return JSON.stringify(details, null, 2) ?? String(details); }
  catch { return String(details); }
}
